// Keep Fit App - 設定頁面

import { useState } from 'react';

interface SettingsProps {
  onLogout?: () => void;
}

export default function Settings({ onLogout }: SettingsProps) {
  const [targets, setTargets] = useState({
    calories: 2000,
    protein: 50,
    fat: 65,
    carbs: 300,
  });

  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    localStorage.setItem('keep-fit-targets', JSON.stringify(targets));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setTargets({
      calories: 2000,
      protein: 50,
      fat: 65,
      carbs: 300,
    });
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      onLogout?.();
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">⚙️ 設定</h1>

      {/* 營養目標 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="font-medium mb-4">🎯 營養目標</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              每日卡路里目標 (kcal)
            </label>
            <input
              type="number"
              value={targets.calories}
              onChange={(e) => setTargets({ ...targets, calories: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-lg p-2"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              每日蛋白質目標 (g)
            </label>
            <input
              type="number"
              value={targets.protein}
              onChange={(e) => setTargets({ ...targets, protein: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-lg p-2"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              每日脂肪目標 (g)
            </label>
            <input
              type="number"
              value={targets.fat}
              onChange={(e) => setTargets({ ...targets, fat: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-lg p-2"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              每日碳水化合物目標 (g)
            </label>
            <input
              type="number"
              value={targets.carbs}
              onChange={(e) => setTargets({ ...targets, carbs: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-lg p-2"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleReset}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg"
          >
            重置預設
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-green-500 text-white py-2 rounded-lg"
          >
            {saved ? '✅ 已保存' : '保存設定'}
          </button>
        </div>
      </div>

      {/* 帳號 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="font-medium mb-3">🔐 帳號</h2>
        <button
          onClick={handleLogout}
          className="w-full bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition"
        >
          登出
        </button>
      </div>

      {/* 關於 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="font-medium mb-3">關於 Keep Fit App</h2>
        <div className="text-sm text-gray-600 space-y-2">
          <p>🍎 AI 驅動嘅飲食追蹤 Web 應用程式</p>
          <p>📸 拍照即可識別食物同營養成分</p>
          <p>📊 追蹤每日/每週營養攝取</p>
        </div>
        
        <div className="border-t mt-4 pt-4 text-xs text-gray-400">
          <p>版本: 1.0.0</p>
          <p>技術棧: React + Deno Deploy + Supabase</p>
          <p>AI: Gemini / DeepSeek / Kiro</p>
        </div>
      </div>

      {/* API 狀態 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="font-medium mb-3">🔧 系統狀態</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">後端 API</span>
            <span className="text-green-600">● 運行中</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">AI 服務</span>
            <span className="text-green-600">● 可用</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">數據庫</span>
            <span className="text-green-600">● 已連接</span>
          </div>
        </div>
      </div>

      {/* 使用說明 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="font-medium mb-3">📖 使用說明</h2>
        <div className="text-sm text-gray-600 space-y-3">
          <div>
            <p className="font-medium text-gray-700">📸 拍照分析</p>
            <p>在「分析」頁面上傳食物照片，AI 會自動識別食物種類並估計營養成分。</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">📝 飲食記錄</p>
            <p>所有分析結果可保存為記錄，按餐類分類管理。</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">📊 營養統計</p>
            <p>查看每日、每週或每月的營養攝取趨勢。</p>
          </div>
        </div>
      </div>
    </div>
  );
}
